import { ChangeDetectorRef, Injectable } from '@angular/core';
import { FormControl, ValuesOf } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  delay,
  exhaustMap,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  pipe,
  startWith,
  switchMap,
  Subscription,
  take,
  tap,
} from 'rxjs';
import { Cart, CartData, ObservationValue } from '../models/cart.models';
import {
  FormOrchestratorTableData,
  FormTableColumns,
  TableFormService,
  UiFormTableComponent,
} from '@brunsker/libs/core/ui/table/src';
import {
  CartDataDisplayColumns,
  ClientData,
  LoginResponse,
  MapperService,
  SelectClientService,
  SelectedClientData,
} from '@brunsker/libs/portal-do-vendedor/utils/portal-vendedor-api';
import type {
  DescontoPrefix,
  IDescontoRodape,
} from '@brunsker/libs/core/ui/table-actions/src/lib/components/desconto/models/desconto.models';
import { DescontoService } from '@brunsker/libs/core/ui/table-actions/src/lib/services/desconto/desconto.service';
import { NewApiOrcamentoService } from '../../../api/new-api-orcamento/new-api-orcamento.service';
import { NewApiUserService } from '../../../api/new-api-user/new-api-user.service';
import { CartObservationsGroupComponent } from '@brunsker/libs/portal-do-vendedor/ui/observations-text-area';
import { FinalizedOrcamentoComponent } from '@brunsker/libs/portal-do-vendedor/ui/finalized-orcamento';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { NewApiProductService } from '../../../api/new-api-product/new-api-product.service';
import { AmountValueService } from '../../../api/amount-value/amount-value.service';
import { ProductLocalStorage } from '@brunsker/libs/portal-do-vendedor/ui/product-card';
import {
  ProdutoDesconto,
  ProdutoResponse,
} from '../../../../models/product.models';
import { packagingFactor } from '../../../../utils/product-packaging';

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class CarrinhoDomainService {
  table?: UiFormTableComponent<any>;
  hasClient = this.selectClientService.hasClient$$;
  columnsOptions: FormTableColumns[] =
    this.mapperService.createCartFormColumnsOptions();
  dataDisplay$!: Observable<
    FormOrchestratorTableData<CartDataDisplayColumns>[]
  >;
  cartTotal$$ = this.selectClientService.cartTotalValue$$;
  cartSubTotal$$ = this.selectClientService.cartSubTotalValue$$;
  totalDiscount$$ = new BehaviorSubject<number>(0);
  keyTable = 'carrinho';
  frete = new FormControl<number>(0);
  descontoRodaPe = new FormControl<IDescontoRodape>({ value: 0, prefix: '%' });
  cart$!: Observable<FormOrchestratorTableData<CartDataDisplayColumns>[]>;
  removeControlsIndex!: string | null;
  hasData!: boolean;
  cartLength!: number;
  ambientes!: string[];
  unitaryValue: number = 0;
  readonly finishButtonEnabled$$ = new BehaviorSubject<boolean>(true);
  readonly isFinishing$$ = new BehaviorSubject<boolean>(false);
  private cartSubTotalSubscription?: Subscription;

  constructor(
    private readonly descontoService: DescontoService,
    private readonly selectClientService: SelectClientService,
    private readonly mapperService: MapperService,
    private readonly tableFormService: TableFormService<Cart>,
    private readonly orcamentoApiService: NewApiOrcamentoService,
    private readonly newApiUserService: NewApiUserService,
    private readonly newApiProductService: NewApiProductService,
    private readonly amountValueService: AmountValueService,
    private dialog: MatDialog,
  ) {}

  start(changeDetectorRef: ChangeDetectorRef) {
    this.tableFormService.keyTable = this.keyTable;

    this.dataDisplay$ = this.selectClientService.updateData$$.pipe(
      this.createDataDisplay(changeDetectorRef),
    );

    this.cartSubTotalSubscription?.unsubscribe();
    this.cartSubTotalSubscription = this.cartSubTotal$$.subscribe(
      (value: number) => {
        sessionStorage.setItem('unitaryValue', value.toString());
      },
    );

    this.frete.setValue(0);
  }

  stop() {
    this.cartSubTotalSubscription?.unsubscribe();
    this.cartSubTotalSubscription = undefined;
    this.table = undefined;
  }
  finishBuy(
    cartObservation: CartObservationsGroupComponent,
    filnalizaOrca?: number | null,
  ) {
    if (this.isFinishing$$.value) return;

    this.isFinishing$$.next(true);
    this.setFinishButtonEnabled(false);

    this.dataDisplay$.pipe(take(1)).subscribe({
      next: (items) => {
        const observationValue = cartObservation.observacaoGroup
          .value as ObservationValue;

        const selectedClient = this.selectClientService.getCliente();

        const produtos = this.mappedProductsResponse(items);

        const formaDeEntrega = selectedClient?.formaDeEntrega?.toString();

        const pID_ENDER_ENTREGA =
          formaDeEntrega == '3' || formaDeEntrega == '4'
            ? 0
            : (selectedClient?.endereco?.ID_ENDERECO ?? 0);

        const user = this.newApiUserService.currentUser();

        const requestData = this.createRequestData(
          selectedClient,
          observationValue,
          filnalizaOrca,
          produtos,
          pID_ENDER_ENTREGA,
          user,
        );

        items?.forEach((item) => {
          const oldValuesID = (
            item.formGroupValue.find(
              (form) => form.formControlName === 'desconto',
            ) as any
          )?.id;
          sessionStorage.removeItem(oldValuesID);
        });

        const numautorizacao = sessionStorage.getItem('numautorizacao');
        const numautorizacaolist =
          numautorizacao &&
          JSON.parse(numautorizacao).filter((item: number) => item > 0);

        this.orcamentoApiService
          .createOrcamento(requestData)
          .pipe(
            take(1),
            finalize(() => this.completeFinishing()),
          )
          .subscribe({
            next: (orcamentoID: number) => {
              this.selectClientService.getTotalCart();
              this.selectClientService.resetCart();
              this.descontoRodaPe.setValue({ value: 0, prefix: '%' });
              this.selectClientService.hasClient$$.next(false);
              this.openDialog(orcamentoID);
              if (numautorizacaolist && numautorizacaolist?.length > 0)
                this.orcamentoApiService
                  .updateDiscountInOrcamento({
                    matricula: +user!.idFuncionario,
                    numorca: orcamentoID,
                    listanumautor: numautorizacaolist,
                  })
                  .subscribe();
            },
            error: () => undefined,
          });
      },
      error: () => this.completeFinishing(),
    });
  }

  setFinishButtonEnabled(enabled: boolean) {
    this.finishButtonEnabled$$.next(enabled);
  }

  private completeFinishing() {
    this.isFinishing$$.next(false);
    this.setFinishButtonEnabled(true);
  }

  openDialog(orcamentoId: number) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.width = 'clamp(500px, 50vw, 50vw)';
    dialogConfig.data = orcamentoId;

    this.dialog.open(FinalizedOrcamentoComponent, dialogConfig);
  }

  createDataDisplay(changeDetectorRef: ChangeDetectorRef) {
    return pipe(
      untilDestroyed(this),
      tap(() => {
        const cart = this.selectClientService.getCart();
        this.cartLength = cart.length;

        const controlsKeys = Object.keys(
          this.tableFormService.formGroups[this.keyTable].controls,
        );

        if (controlsKeys.length <= 0) return;
        controlsKeys.forEach((key, index) => {
          const controls =
            this.tableFormService.formGroups[this.keyTable].controls;
          if (
            controls[key] &&
            controls[key].controls['quantidade'] &&
            controls[key].controls['retiradaImediata'] &&
            controls[key].controls['remove'] &&
            cart[index]
          )
            controls[key].controls['retiradaImediata']?.setValue(
              cart[index]?.qtd,
              {
                emitEvent: false,
              },
            );
          controls[key].controls['remove']?.setValue(false, {
            emitEvent: false,
          });
        });
      }),
      this.createTableFormData(changeDetectorRef),
    );
  }

  createTableFormData(changeDetectorRef: ChangeDetectorRef) {
    return pipe(
      startWith(true),
      exhaustMap(() => {
        return this.tableFormService.formGroups[this.keyTable].valueChanges
          .pipe(
            startWith(this.tableFormService.formGroups[this.keyTable].value),
            untilDestroyed(this),
            switchMap((_value, index: number) => {
              const getSessionStorage = this.selectClientService.getCart();

              const lastAmbient = this.ambientes;

              const replaceSessionStorage: Cart[] = getSessionStorage.map(
                (cart: CartData, storageIndex: number) =>
                  this.mapReplaceData(cart, storageIndex),
              );

              const removeProducts = replaceSessionStorage.filter(
                (cart: { id: number; remove: boolean }, index) => {
                  if (cart.remove) {
                    sessionStorage.removeItem(`${index}-${cart.id}-ca`);
                    setTimeout(() => {
                      this.descontoRodaPe.setValue(this.descontoRodaPe.value);
                    }, 1000);
                  }
                  const isRemove = !cart.remove;
                  return isRemove;
                },
              );

              removeProducts.forEach((item, index) => {
                if (
                  replaceSessionStorage[index].id === item.id &&
                  replaceSessionStorage[index].desconto === item.desconto
                )
                  return;
                sessionStorage.setItem(
                  `${index}-${item.id}-ca`,
                  '' + item.desconto,
                );
                sessionStorage.removeItem(`${index + 1}-${item.id}-ca`);
              });

              let totalCart = 0;
              removeProducts.forEach((itemCart) => {
                totalCart += itemCart.qtd;
              });

              sessionStorage.setItem('totalItemsCart', `${totalCart}`);

              if (totalCart)
                this.selectClientService.totalItemsCart$$.next(totalCart);

              this.ambientes = removeProducts.reduce(
                (acc: string[], product: Cart) => {
                  if (!product.observacao.ambiente) return [...acc];
                  acc.push(product.observacao.ambiente);
                  return acc;
                },
                [],
              );

              const ambienteEquals = lastAmbient
                ?.map((ambiente) => {
                  return this.ambientes.includes(ambiente);
                })
                .includes(false);

              if (
                lastAmbient?.length !== this.ambientes?.length ||
                ambienteEquals
              )
                this.cartLength++;

              const totalDesconto = removeProducts.reduce(
                (
                  acc: number,
                  cart: {
                    precoComDesconto: number;
                    preco: number;
                    qtd: number;
                  },
                ) => {
                  return acc + (cart.preco - cart.precoComDesconto) * cart.qtd;
                },
                0,
              );

              this.totalDiscount$$.next(+totalDesconto.toFixed(2));

              let rodaPe = this.descontoRodaPe.value;
              if (!rodaPe) {
                rodaPe = {
                  value: 0,
                  prefix: '%',
                };
              }

              const valid =
                typeof rodaPe == 'object'
                  ? rodaPe.value
                  : +(rodaPe + '').replace(',', '.');
              if (!!!valid) {
                this.descontoService.descontoTotalVlTotal$.setValue({
                  vlTotal: this.cartTotal$$.value - totalDesconto,
                });
              }
              this.selectClientService.replaceCart(removeProducts);
              this.selectClientService.getTotalCart();

              const updatedGetSessionStorage =
                this.selectClientService.getCart();

              const tableData = this.createTableData(
                updatedGetSessionStorage,
                index,
              );

              return of(tableData);
            }),
          )
          .pipe(delay(0), this.refreshNewTableData(changeDetectorRef));
      }),
    );
  }

  refreshNewTableData(changeDetectorRef: ChangeDetectorRef) {
    return pipe(
      tap((value: FormOrchestratorTableData<CartDataDisplayColumns>[]) => {
        this.hasData = value.length > 0;

        if (!this.table) return;

        this.table.dataSource = value;
        this.table.bsFormOrchestrator.formOrchestratorData = value;
        this.tableFormService.tableData$$?.next(value as unknown as Cart[]);
        this.table.changeDetectorRef.detectChanges();
        changeDetectorRef.detectChanges();

        if (this.cartLength !== value.length) {
          this.cartLength = value.length;
          this.table.bsFormOrchestrator.ngOnInit();
        }
      }),
    );
  }

  reloadCartForClient(
    selectedClient: SelectedClientData,
  ): Observable<ProductLocalStorage[]> {
    const cart = this.selectClientService.getCart() as ProductLocalStorage[];

    if (!cart.length) return of([]);

    const user = this.newApiUserService.currentUser();
    const requests = cart.map((item) =>
      forkJoin({
        product: this.newApiProductService.getProductById({
          Matricula: user!.idFuncionario.toString(),
          CodigoProduto: item.id.toString(),
          CodigoFilial: selectedClient.selectFilial.toString(),
          CodigoCliente: selectedClient.selectClient.toString(),
          CodigoUsuario: selectedClient.selectVendedor.toString(),
          CodigoPlanoPagamento: selectedClient.selectPrazo,
          CodigoCobranca: selectedClient.selectPagamento,
          Embalagem:
            item.embalagemSelecionada && `${item.embalagem} - ${item.unidade}`,
          OrigemPedido: selectedClient.selectOrigemVenda,
        }),
        discountRule: this.newApiProductService.getProductDiscount({
          CodigoFilial: selectedClient.selectFilial,
          CodigoCliente: selectedClient.selectClient,
          CodigoVendedor: selectedClient.selectVendedor,
          CodigoParcela: selectedClient.selectPrazo,
          CodigoProduto: item.id,
          matricula: user!.idFuncionario,
        }),
      }),
    );

    return forkJoin(requests).pipe(
      map((responses) =>
        cart.map((item, index) =>
          this.applyClientRulesToCartItem(
            item,
            responses[index].product,
            responses[index].discountRule,
          ),
        ),
      ),
      tap((newCart) => {
        this.resetPreviousClientDiscountState(cart);
        this.selectClientService.replaceCart(newCart);
        this.synchronizeCartForm(newCart);
        this.updateCartTotals(newCart);
      }),
    );
  }

  clearCartState() {
    const cart = this.selectClientService.getCart() as ProductLocalStorage[];
    this.resetPreviousClientDiscountState(cart);

    const cartForm = this.tableFormService.formGroups[this.keyTable];
    Object.keys(cartForm?.controls ?? {}).forEach((key) => {
      cartForm.removeControl(key, { emitEvent: false });
    });

    this.cartLength = 0;
    this.hasData = false;
    this.totalDiscount$$.next(0);
    this.descontoService.descontoTotalVlTotal$.setValue({ vlTotal: 0 });
  }

  private applyClientRulesToCartItem(
    item: ProductLocalStorage,
    product: ProdutoResponse,
    discountRule: Pick<
      ProdutoDesconto,
      'percdesc' | 'descautomatico' | 'iD_DESCONTO'
    > &
      Partial<ProdutoDesconto>,
  ): ProductLocalStorage {
    const basePrice = Number(
      (+product.preco * packagingFactor(item.embalagemSelecionada)).toFixed(8),
    );
    const unitaryPrice = +(
      basePrice *
      (1 + this.amountValueService.AMOUNT_VALUE)
    ).toFixed(2);
    const maxDiscount: ProdutoDesconto = {
      ...product.descontoMaximoResponse,
      ...discountRule,
    };
    const authorizedDiscount = +maxDiscount?.percdescautor;
    const maximumDiscount =
      authorizedDiscount > 0 ? authorizedDiscount : +maxDiscount?.percdesc || 0;
    const discount = maxDiscount?.descautomatico === 'S' ? maximumDiscount : 0;
    const priceWithDiscount = +(unitaryPrice * (1 - discount / 100)).toFixed(2);

    return {
      ...item,
      preco: unitaryPrice,
      olderPrice: basePrice,
      precoComDesconto: priceWithDiscount,
      desconto: discount,
      percentual: discount,
      descontoMaximo: maxDiscount,
      subTotal: +(priceWithDiscount * item.qtd).toFixed(2),
      stock: +product.qT_DISPONIVEL,
      filial: product.iD_FILIAL ?? +item.filial,
      multiplo: item.embalagemSelecionada ? '1' : product.multiplo,
      embalagem:
        item.embalagemSelecionada?.ean ?? product.embalagem ?? item.embalagem,
      embalagensprod: product.embalagensprod,
      unidade:
        item.embalagemSelecionada?.unidadeDeVenda ??
        product.unidademaster ??
        item.unidade,
      valorDescontoAntigo: +product.valorDescontoAntigo || 0,
      remove: false,
    };
  }

  private resetPreviousClientDiscountState(cart: ProductLocalStorage[]) {
    cart.forEach((item, index) => {
      sessionStorage.removeItem(`${index}-${item.id}-ca`);
    });
    sessionStorage.removeItem('cart-rodape');
    sessionStorage.removeItem('numautorizacao');
    sessionStorage.removeItem('prefix');

    const emptyDiscount = { value: 0, prefix: '%' as const };
    this.descontoRodaPe.setValue(emptyDiscount, { emitEvent: false });
    this.descontoService.descontoRodapeValue$.setValue(emptyDiscount, {
      emitEvent: false,
    });
    this.descontoService.changeDescontoRodape$$.next(emptyDiscount);
  }

  private synchronizeCartForm(cart: ProductLocalStorage[]) {
    const cartForm = this.tableFormService.formGroups[this.keyTable];

    if (!cartForm) return;

    cart.forEach((item, index) => {
      const itemForm = cartForm.get(index.toString());

      if (!itemForm) return;

      itemForm.patchValue(
        {
          desconto: { value: item.percentual, prefix: '%' },
          valorUnitario: item.preco,
          valorUnitarioComDesconto: item.precoComDesconto,
          subTotal: item.subTotal,
          remove: false,
        },
        { emitEvent: false },
      );
    });

    cartForm.updateValueAndValidity({ emitEvent: true });
  }

  private updateCartTotals(cart: ProductLocalStorage[]) {
    const totalDiscount = cart.reduce(
      (total, item) => total + (item.preco - item.precoComDesconto) * item.qtd,
      0,
    );
    const discountedTotal = cart.reduce(
      (total, item) => total + item.subTotal,
      0,
    );

    this.totalDiscount$$.next(+totalDiscount.toFixed(2));
    this.descontoService.descontoTotalVlTotal$.setValue({
      vlTotal: +discountedTotal.toFixed(2),
    });
    this.selectClientService.getTotalCart();
  }

  mapReplaceData(cart: CartData, storageIndex: number) {
    const stringIndex = storageIndex.toString();

    const formGroup =
      this.tableFormService.formGroups[this.keyTable].get(stringIndex);

    if (!formGroup || !formGroup.value) return cart;

    const item = formGroup.value;

    item['desconto'] =
      typeof item['desconto'] === 'object'
        ? item['desconto']
        : { value: item['desconto'], prefix: '%' };

    const fnDesconto = this.fnDesconto(cart, item);

    const fnSaveDesconto = this.fnSaveDesconto(cart, item);

    cart.desconto = fnSaveDesconto(item['desconto']);
    cart.precoComDesconto = fnDesconto(item['desconto']);

    cart.percentual = this.setPercent(item, cart);

    cart.qtd = item['quantidade'];
    cart.retiradaImediata = Boolean(JSON.parse(item['retiradaImediata']));
    cart.observacao = item['observation'];
    cart.subTotal = Number(
      (cart.precoComDesconto * +item['quantidade']).toFixed(2),
    );
    cart.remove = !!item['remove'];

    if (item['quantidade'] === true) cart.remove = true;
    if (cart.remove) this.removeControlsIndex = stringIndex;

    this.selectClientService.saveDesconto(cart, storageIndex);

    const subTotalPredicate = this.subTotalPredicate(
      storageIndex,
      item,
      fnDesconto,
    );

    const updateDataByIndex =
      this.tableFormService.tableData$$.value.map(subTotalPredicate);

    this.tableFormService.tableData$$.next(updateDataByIndex);

    return cart;
  }

  setPercent(product: { [key: string]: any }, cart: any): number {
    const item = product['desconto'];

    if (!item) return 0;

    if (!item.value) return 0;

    if (item['prefix'] === 'R$') {
      const unitaryValue = +cart.olderPrice;

      const percentage =
        ((unitaryValue - item.value) * 100) / unitaryValue - 100;

      return +(percentage * -1);
    }

    return item.value;
  }

  private subTotalPredicate(
    storageIndex: number,
    item: ValuesOf<{ [key: string]: FormControl<any> }>,
    fnDesconto: (desconto: { value: any; prefix: DescontoPrefix }) => number,
  ) {
    return (data: Cart, controlIndex: number) => {
      if (storageIndex === controlIndex) {
        const valorUnitarioEditado = fnDesconto(item['desconto']);

        data.precoComDesconto = valorUnitarioEditado;
        data.valorUnitarioComDesconto = valorUnitarioEditado;
        data.subTotal = Number(
          (valorUnitarioEditado * +item['quantidade']).toFixed(2),
        );
      }
      return data;
    };
  }

  private fnSaveDesconto(
    cart: CartData,
    item: ValuesOf<{ [key: string]: FormControl<any> }>,
  ) {
    return (desconto: { value: any; prefix: DescontoPrefix }) => {
      if (!desconto) return 0;

      if (!desconto.value && desconto.value !== 0) return cart.desconto;

      const removeComma = desconto.value.toString().replace(',', '.');
      const valorUnitario = item['valorUnitario'];

      if (desconto.prefix === '%') return +removeComma;

      const calcPercentage = (removeComma / valorUnitario) * 100;

      const percentageDiscountToFixed = calcPercentage;

      return +percentageDiscountToFixed;
    };
  }

  private fnDesconto(
    cart: CartData,
    item: ValuesOf<{ [key: string]: FormControl<any> }>,
  ) {
    return (desconto: { value: any; prefix: DescontoPrefix }) => {
      if (!desconto) return 0;

      if (!!!desconto.value && desconto.value !== 0)
        return cart.precoComDesconto;
      const removeComma = desconto.value.toString().replace(',', '.');
      const valorUnitario = item['valorUnitario'];

      if (desconto.prefix !== '%') return valorUnitario - +removeComma;
      const calcPercentage = (valorUnitario / 100) * +removeComma;
      const valorUnitarioComDesconto = valorUnitario - calcPercentage;
      return valorUnitarioComDesconto;
    };
  }

  createTableData(products: Cart[], index: number) {
    const MAP_DATA_COLUMNS = products.map((product, index) => {
      // A tabela relaciona os campos visíveis pela posição. Por isso, estes
      // campos devem seguir a mesma ordem de columnsOptions; metadados ficam
      // depois de `remove` para não deslocarem os valores apresentados.
      return {
        counter: index + 1,
        image: product.image,
        produto: product.produto,
        embalagem: product.embalagemSelecionada
          ? `${product.embalagemSelecionada.nomeEmbalagem} - ${product.embalagemSelecionada.unidadeDeVenda}`
          : `${product.embalagem} - ${product.unidade}`,
        qtd: product.qtd,
        desconto: product.desconto,
        valorUnitario: product.preco,
        valorUnitarioComDesconto: product.precoComDesconto,
        subTotal: product.subTotal,
        observacao: product.observacao,
        remove: false,
        eanEmbalagem: product.embalagemSelecionada?.ean,
        unidade: product.unidade,
        retiradaImediata: product.retiradaImediata,
        filial: product.filial,
        id: index,
        sku: product.id,
        produtoNome: product.produto.replace(/^\s*\d+\s*-\s*/, '').trim(),
        formGroupValue: [
          {
            formControlName: 'counter',
            value: index + 1,
          },
          {
            formControlName: 'image',
            value: product.image,
          },
          {
            formControlName: 'produto',
            value: product.produto,
          },
          {
            formControlName: 'embalagem',
            value: product.embalagemSelecionada
              ? `${product.embalagemSelecionada.nomeEmbalagem} - ${product.embalagemSelecionada.unidadeDeVenda}`
              : `${product.embalagem} - ${product.unidade}`,
          },
          {
            formControlName: 'quantidade',
            value: product.qtd,
            hasMultiple: product.multiplo,
          },
          {
            formControlName: 'desconto',
            value: product.percentual,
            currentDiscount: product.percentual,
            unitaryValue: product.preco,
            maxPercentDiscount: product.descontoMaximo,
            descontoRodaPe: this.descontoRodaPe,
            oldValue: sessionStorage.getItem(`${index}-${product.id}-ca`),
            id: `${index}-${product.id}-ca`,
          },
          {
            formControlName: 'valorUnitario',
            value: product.preco,
          },
          {
            formControlName: 'valorUnitarioComDesconto',
            value: product.precoComDesconto,
          },
          {
            formControlName: 'subTotal',
            value: product.subTotal,
          },
          {
            formControlName: 'observation',
            value: product.observacao,
            isEditable: true,
            ambientes: this.ambientes,
          },
          {
            formControlName: 'remove',
            value: false,
            content: {
              h1: 'Remover produto',
              p: 'Deseja realmente remover o produto?',
              button: 'Remover do carrinho',
            },
          },
          {
            formControlName: 'retiradaImediata',
            value: product.retiradaImediata,
            isEditable: false,
          },
        ],
      } as FormOrchestratorTableData<CartDataDisplayColumns>;
    });

    return MAP_DATA_COLUMNS;
  }

  private mappedProductsResponse(
    items: FormOrchestratorTableData<CartDataDisplayColumns>[],
  ) {
    return items.map((item) => {
      const calcDiscountPercentage = item.desconto;

      const fixedDiscount = +calcDiscountPercentage;

      const valorDescontoAntigo = (
        item.formGroupValue.find(
          (form) => form.formControlName === 'desconto',
        ) as any
      )?.id;
      const perdescaux = +sessionStorage.getItem(valorDescontoAntigo)!;

      return {
        pindex: null,
        piD_PRODUTO: item.sku,
        pqtproduto: item.qtd,
        pvlproduto: item.valorUnitario * (1 - item.desconto / 100),
        pvlprodutotabela: item.valorUnitario,
        pobservacaO_ITEM: item.observacao.observacao ?? '',
        pvldesc: item.desconto,
        pvlprodutobase: item.valorUnitario,
        retiradaImediata: item.retiradaImediata,
        pperdesc: fixedDiscount,
        perdescaux: perdescaux,
        qtproduto: item.qtd,
        iD_FILIAL: item.filial,
        pID_PRODUTO: item.sku,
        qT_DISPONIVEL: item.qtd,
        pAMBIENTE: item.observacao.ambiente ?? '',
        pUNIDADE: item.unidade,
        pEMBALAGEM: item.eanEmbalagem ?? item.embalagem,
      };
    });
  }

  private createRequestData(
    selectedClient: ClientData | null,
    observationValue: ObservationValue,
    gerarPed: number | unknown,
    produto: {
      piD_PRODUTO: number;
      pqtproduto: number;
      pvlproduto: number;
      pvlprodutotabela: number;
      pobservacaO_ITEM: string;
      pvldesc: number;
      pvlprodutobase: number;
      pperdesc: number;
      pAMBIENTE: string;
      qtproduto: number;
      iD_FILIAL: number;
      pID_PRODUTO: number;
      qT_DISPONIVEL: number;
      retiradaImediata: boolean;
      pUNIDADE?: string;
      pEMBALAGEM?: string;
    }[],
    pID_ENDER_ENTREGA: number,
    user: LoginResponse | null,
  ) {
    return {
      gerarPed: gerarPed,
      piD_CLIENTE: selectedClient?.id,
      piD_VENDEDOR: selectedClient?.vendedor,
      pfilial: selectedClient?.filial,
      pcobranca: selectedClient?.formaDePagamento,
      pmatricula: user?.idFuncionario,
      pSTATUSPENDENCIAORC: null,
      origenvenda: selectedClient?.origemVenda,
      pparcela: selectedClient?.planoDePagamento,
      ptipovenda: selectedClient?.tipoVenda,
      pcodprofissional: selectedClient?.indication,
      pobservacaoent: observationValue.entrega ?? null,
      pobservacaoent2: observationValue.entrega2 ?? null,
      pobservacaoent3: observationValue.entrega3 ?? null,
      obS_NOTA: observationValue.nota,
      pSTATUS: 0,
      pOBSERVACAO: observationValue.orcamento,
      pOBSERVACAO2: observationValue.orcamento2,
      pOBSERVACAO3: observationValue.orcamento3,
      pDTENTREGA: null,
      pvlfrete: typeof this.frete.value == 'string' ? 0 : this.frete.value,
      pOBSERVACAO_ITEM: null,
      pTRANSPORTE: selectedClient?.formaDeEntrega,
      pseqpedidoentfut: null,
      pcpfcgc: selectedClient?.data.CPFCNPJ,
      pENDERECOENTREGA:
        selectedClient?.endereco?.LOGRADOURO_ENTREGA ??
        selectedClient?.data?.LOGRADOURO_ENTREGA ??
        '',
      pNUMEROENTREGA:
        selectedClient?.endereco?.NUMERO ??
        selectedClient?.data?.NUMERO_ENTREGA ??
        '',
      pCOMPLEMENTOENTREGA:
        selectedClient?.endereco?.COMPLEMENTO_ENTREGA ??
        selectedClient?.data?.COMPLEMENTO_ENTREGA ??
        '',
      pBAIRROENTREGA:
        selectedClient?.endereco?.BAIRRO_ENTREGA ??
        selectedClient?.data?.BAIRRO_ENTREGA ??
        '',
      pCIDADEENTREGA:
        selectedClient?.endereco?.CIDADE_ENTREGA ??
        selectedClient?.data?.CIDADE_ENTREGA ??
        '',
      pUFENTREGA:
        selectedClient?.endereco?.UF_ENTREGA ??
        selectedClient?.data?.UF_ENTREGA ??
        '',
      pCEPENTREGA:
        selectedClient?.endereco?.CEP_ENTREGA ??
        selectedClient?.data?.CEP_ENTREGA ??
        '',
      produtos: produto,
      pID_ENDER_ENTREGA: pID_ENDER_ENTREGA,
      pSEQ_FORMAENTREGA: selectedClient?.formaDeEntrega?.toString(),
      pAMBIENTE: '',
      percentualDescontoRodape:
        this.saveRodaPePercentual() <= 0 ? null : this.saveRodaPePercentual(),
    };
  }

  saveRodaPePercentual() {
    if (!this.descontoRodaPe.value.value) return 0;

    const isPercent = sessionStorage.getItem('prefix') === '%';

    if (!isPercent) return this.saveValorDescontoRodaPe();

    return +this.descontoRodaPe.value.value;
  }

  saveValorDescontoRodaPe() {
    const { vlTotal } = this.descontoService.descontoTotalVlTotal$.value;
    const realDesconto =
      +this.descontoRodaPe.value.value < 0
        ? +this.descontoRodaPe.value.value * -1
        : this.descontoRodaPe.value.value;

    const percent = ((vlTotal - +realDesconto - vlTotal) / vlTotal) * 100;
    const percentPosi = percent < 0 ? percent * -1 : percent;
    sessionStorage.setItem('rodaPePercent', `${percentPosi}`);
    return percentPosi;
  }
}
